import React, { useState } from "react";
import { Search, Plus, Minus } from "lucide-react";
import FacilityConstants from "../../../utilities/constants/FacilityConstants";

const StepExtras = ({ onNext, onPrev, initialData, addOnsData, loading }) => {
  const [selectedAddOns, setSelectedAddOns] = useState(
    initialData.addOns || [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilters, setCategoryFilters] = useState([]);

  // Filter out rate-config and internal AddOns — only show customer-selectable ones
  const customerAddOns = addOnsData.filter(
    (item) =>
      (!item.addonType || item.addonType === FacilityConstants.ADDON_TYPE.CUSTOMER),
  );

  // Filter Logic matching AddOnsList.js
  const categories = customerAddOns
    .reduce((uniqueCategories, item) => {
      if (!uniqueCategories.find((c) => c.value === item.category)) {
        uniqueCategories.push({
          value: item.category,
          label: item.category.charAt(0).toUpperCase() + item.category.slice(1),
        });
      }
      return uniqueCategories;
    }, []);

  const filteredItems = customerAddOns.filter((item) => {
    if (categoryFilters.length > 0 && !categoryFilters.includes(item.category))
      return false;
    if (
      searchQuery &&
      !item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    return item.active === true;
  });

  const handleQuantityChange = (item, delta) => {
    const currentList = [...selectedAddOns];
    const index = currentList.findIndex((x) => x.addOn.name === item.name);

    if (index >= 0) {
      const newQuantity = Math.max(0, currentList[index].quantity + delta);
      if (newQuantity === 0) {
        currentList.splice(index, 1);
      } else {
        currentList[index].quantity = newQuantity;
      }
    } else if (delta > 0) {
      currentList.push({ addOn: item, quantity: 1 });
    }
    setSelectedAddOns(currentList);
  };

  // Calculate total for display
  const currentTotal = selectedAddOns.reduce(
    (acc, curr) => acc + curr.addOn.price * curr.quantity,
    0,
  );

  const handleSubmit = () => {
    onNext({ addOns: selectedAddOns, addOnsTotalCost: currentTotal });
  };

  const handleCategoryFilterChange = (category) => {
    if (categoryFilters.includes(category)) {
      setCategoryFilters(categoryFilters.filter((c) => c !== category));
    } else {
      setCategoryFilters([...categoryFilters, category]);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-6 font-alata text-center">
        Enhance Your Experience
      </h3>

      <div className="flex flex-col md:flex-row gap-6 h-[500px]">
        {/* Sidebar Filters */}
        <div className="w-full md:w-1/4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search extras..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 p-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
            />
          </div>
          <div>
            <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
              Categories
            </h4>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryFilterChange(cat.value)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    categoryFilters.includes(cat.value)
                      ? "bg-blue-100 dark:bg-blue-900 border-blue-500 text-blue-700 dark:text-blue-200"
                      : "border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-slate-800 p-4 rounded-xl border border-blue-100 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Current Extras Total
            </p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              ${currentTotal.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Items Grid */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {loading ? (
            <div className="flex justify-center items-center h-full text-slate-400">
              Loading options...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => {
                const qty =
                  selectedAddOns.find((x) => x.addOn.name === item.name)
                    ?.quantity || 0;
                return (
                  <div
                    key={item.id}
                    className={`p-4 rounded-xl border transition-all ${qty > 0 ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300"}`}
                  >
                    <div className="h-32 mb-3 bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
                      <img
                        src={item.imageSource}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4
                      className="font-bold text-slate-900 dark:text-white truncate"
                      title={item.name}
                    >
                      {item.name}
                    </h4>
                    <p
                      className="text-sm text-slate-500 dark:text-slate-400 mb-3 line-clamp-2"
                      title={item.description}
                    >
                      {item.description}
                    </p>
                    <div className="flex justify-between items-center mt-auto">
                      <span className="font-bold text-blue-600 dark:text-blue-400">
                        ${item.price}
                      </span>
                      <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 rounded-lg p-1">
                        <button
                          onClick={() => handleQuantityChange(item, -1)}
                          className="p-1 hover:bg-white dark:hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50"
                          disabled={qty === 0}
                        >
                          <Minus className="w-3 h-3 text-slate-600 dark:text-slate-300" />
                        </button>
                        <span className="text-xs font-bold w-4 text-center text-slate-900 dark:text-white">
                          {qty}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item, 1)}
                          className="p-1 hover:bg-white dark:hover:bg-slate-700 rounded-md transition-colors"
                        >
                          <Plus className="w-3 h-3 text-slate-600 dark:text-slate-300" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t border-slate-200 dark:border-slate-700 mt-6">
        <button
          type="button"
          onClick={onPrev}
          className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-8 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

export default StepExtras;
